'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Toaster } from 'react-hot-toast'
import { useCartStore } from '@/store/cartStore'
import { isShoppingPage, STICKY_CART_BAR_HEIGHT } from '@/components/cart/StickyCartBar'

/**
 * Global toast host that keeps toasts clear of the mobile sticky cart bar.
 *
 * The bar only renders on mobile/tablet (< lg), on shopping pages, when the
 * cart has items and the drawer is closed — exactly the same conditions as
 * StickyCartBar. When it's showing we lift the toast container above it so a
 * toast (add-to-cart, stock alert, …) never clips over the bar.
 */
export default function AppToaster() {
  const pathname = usePathname()
  const { isOpen, totalItems } = useCartStore()
  const [mounted, setMounted] = useState(false)
  const [isCompact, setIsCompact] = useState(false)

  useEffect(() => {
    setMounted(true)
    const mq = window.matchMedia('(max-width: 1023px)')
    const sync = () => setIsCompact(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const count = mounted ? totalItems() : 0
  const barVisible =
    mounted && isCompact && count > 0 && !isOpen && isShoppingPage(pathname)

  /* Base inset (matches react-hot-toast's default 16px gutter) + the bar's
     height (plus the safe-area inset) whenever the bar is on screen. */
  const bottom = barVisible
    ? `calc(env(safe-area-inset-bottom, 0px) + ${STICKY_CART_BAR_HEIGHT + 16}px)`
    : 16

  return (
    <Toaster
      position="bottom-right"
      containerStyle={{
        bottom,
        transition: 'bottom 0.25s ease',
      }}
      toastOptions={{
        duration: 3500,
        style: {
          background:   '#FDF6EC',
          color:        '#2C1810',
          border:       '1px solid #D2691E',
          borderRadius: '12px',
          fontFamily:   "var(--font-nunito), 'Nunito', sans-serif",
          fontSize:     '0.9rem',
          boxShadow:    '0 4px 14px rgba(139, 69, 19, 0.18)',
        },
        success: {
          iconTheme: { primary: '#8B4513', secondary: '#FDF6EC' },
        },
        error: {
          iconTheme: { primary: '#DC2626', secondary: '#FDF6EC' },
        },
      }}
    />
  )
}
